import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, GitCommitHorizontal, Trash2, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";

export default function PipelineDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [yamlText, setYamlText] = useState("");
  const [conns, setConns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [committing, setCommitting] = useState(false);

  const load = () =>
    api.get(`/pipelines/${id}`).then((r) => { setP(r.data); setYamlText(r.data.yaml_content); });

  useEffect(() => {
    load().catch(() => { toast.error("Pipeline não encontrada"); navigate("/app/pipelines"); });
    api.get("/connections").then((r) => setConns(r.data));
  }, [id]); // eslint-disable-line

  if (!p) return <div className="p-8 text-zinc-500">carregando...</div>;

  const matchingConn = conns.find((c) => c.provider === p.provider);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/pipelines/${id}`, { yaml_content: yamlText });
      toast.success("Alterações salvas");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const commit = async () => {
    if (!matchingConn) { toast.error(`Conecte o ${p.provider} primeiro`); return; }
    setCommitting(true);
    try {
      const { data } = await api.post(`/pipelines/${id}/commit`, { connection_id: matchingConn.id });
      toast.success("Pipeline enviada ao repositório");
      setP((prev) => ({ ...prev, status: "committed", committed_url: data.committed_url }));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/pipelines/${id}`);
      toast.success("Pipeline excluída");
      navigate("/app/pipelines");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-8 fadeup">
      <button onClick={() => navigate("/app/pipelines")} className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-acid mb-4">
        <ArrowLeft size={14} /> Pipelines
      </button>
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">{p.provider} · {p.branch} · {p.language}</div>
          <h1 className="text-4xl font-black tracking-tighter">{p.repo_full_name}</h1>
        </div>
        <span data-testid="pipeline-status" className={`text-xs uppercase tracking-widest border px-3 py-1 ${p.status === "committed" ? "border-acid text-acid" : "border-zinc-600 text-zinc-400"}`}>{p.status}</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {p.tools.map((t) => (
          <span key={t} className="text-xs uppercase tracking-widest border border-acid/40 text-acid bg-acid/10 px-2 py-1">{t}</span>
        ))}
        {p.tools.length === 0 && <span className="text-xs text-zinc-500">sem ferramentas selecionadas</span>}
      </div>

      {p.committed_url && (
        <a href={p.committed_url} target="_blank" rel="noreferrer" data-testid="committed-link"
          className="inline-flex items-center gap-2 text-sm text-acid hover:underline mb-6">
          <CheckCircle2 size={16} /> Ver commit no repositório <ExternalLink size={14} />
        </a>
      )}

      <div className="border border-line bg-surface">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-xs uppercase tracking-widest text-zinc-500">{p.file_path}</span>
        </div>
        <textarea data-testid="detail-yaml-editor" value={yamlText} onChange={(e) => setYamlText(e.target.value)} spellCheck={false}
          className="w-full min-h-[460px] bg-void text-acid/90 text-xs leading-relaxed p-4 focus:outline-none resize-none font-mono" />
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        <button data-testid="detail-save-btn" onClick={save} disabled={saving}
          className="border border-line px-6 py-3 text-sm uppercase tracking-widest hover:border-acid hover:text-acid transition-colors flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Salvar
        </button>
        <button data-testid="detail-commit-btn" onClick={commit} disabled={committing || !matchingConn}
          className="bg-acid text-black font-bold px-6 py-3 text-sm uppercase tracking-widest hover:bg-[#32e612] transition-colors flex items-center gap-2 disabled:opacity-50"
          title={matchingConn ? "" : `Conecte o ${p.provider}`}>
          {committing ? <Loader2 className="animate-spin" size={16} /> : <GitCommitHorizontal size={16} />} Enviar ao repositório
        </button>
        <button data-testid="detail-delete-btn" onClick={remove}
          className="border border-line px-6 py-3 text-sm uppercase tracking-widest text-zinc-400 hover:border-destructive hover:text-destructive transition-colors flex items-center gap-2 ml-auto">
          <Trash2 size={16} /> Excluir
        </button>
      </div>
    </div>
  );
}
